import type { BoxProps } from "@mui/material/Box"
import type { Breakpoint } from "@mui/material/styles"
import type { ContainerProps } from "@mui/material/Container"

import useIsMobile from "@/src/hooks/useIsMobile"
import { Iconify } from "@/src/components/iconify"
import { isJobDetails } from "@/src/utils/storage-available"

import Box from "@mui/material/Box"
import { useTheme } from "@mui/material/styles"
import Container from "@mui/material/Container"
import IconButton from "@mui/material/IconButton"

import { layoutClasses } from "src/layouts/classes"

import { useSettingsContext } from "src/components/settings"
// ----------------------------------------------------------------------

type MainProps = BoxProps & {
    isNavHorizontal: boolean
    mobileNavOpen: () => void
}

export function Main({ children, isNavHorizontal, sx, mobileNavOpen, ...other }: MainProps) {
    const theme = useTheme()
    const isMobile = useIsMobile()

    return (
        <Box
            component="main"
            className={layoutClasses.main}
            sx={{
                display: "flex",
                flex: "1 1 auto",
                pt: isMobile ? "0" : "var(--layout-header-desktop-height)",
                flexDirection: "column",
                ...(isNavHorizontal && {
                    "--layout-dashboard-content-pt": "40px",
                }),
                backgroundColor: theme.palette.background.neutral,
                ...sx,
            }}
            {...other}
        >
            {isMobile && !isJobDetails() && (
                <IconButton onClick={() => mobileNavOpen?.()} sx={{ width: "fit-content", alignSelf: "flex-start" }}>
                    <Iconify icon="mdi:menu" />
                </IconButton>
            )}
            {children}
        </Box>
    )
}

// ----------------------------------------------------------------------

type DashboardContentProps = ContainerProps & {
    disablePadding?: boolean
}

export function DashboardContent({ sx, children, disablePadding, maxWidth = "xl", ...other }: DashboardContentProps) {
    const theme = useTheme()

    const settings = useSettingsContext()

    const layoutQuery: Breakpoint = "lg"

    return (
        <Container
            className={layoutClasses.content}
            maxWidth={settings.compactLayout ? maxWidth : false}
            sx={{
                display: "flex",
                flex: "1 1 auto",
                flexDirection: "column",
                pt: "var(--layout-dashboard-content-pt)",
                pb: "var(--layout-dashboard-content-pb)",
                [theme.breakpoints.up(layoutQuery)]: {
                    px: "var(--layout-dashboard-content-px)",
                },
                backgroundColor: theme.palette.background.neutral,
                ...(disablePadding && {
                    p: {
                        xs: 0,
                        sm: 0,
                        md: 0,
                        lg: 0,
                        xl: 0,
                    },
                }),
                ...sx,
            }}
            {...other}
        >
            {children}
        </Container>
    )
}
